import { useCallback, useState } from 'react';
import { keepPreviousData, useInfiniteQuery, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import type { CharacterView, ChatMessagesResponse, Command, CommandResult, HouseView, Id, TitleRank, TitleSummary } from '@pk/shared';
import { useTranslation } from 'react-i18next';
import { api } from './client';
import { setToken } from './token';
import { useUi } from '../store';

export const qk = {
  me: ['me'] as const,
  world: ['world'] as const,
  house: ['house'] as const,
  character: (id: Id) => ['character', id] as const,
  title: (id: Id) => ['title', id] as const,
  houseDetail: (id: Id) => ['houseDetail', id] as const,
  chronicle: (mine: boolean) => ['chronicle', mine] as const,
  pedigree: (id: Id) => ['pedigree', id] as const,
  titleHistory: (id: Id) => ['titleHistory', id] as const,
  characterHistory: (id: Id) => ['characterHistory', id] as const,
  cities: ['cities'] as const,
  frontier: ['frontier'] as const,
  market: (cityId: Id) => ['market', cityId] as const,
  guild: (cityId: Id) => ['guild', cityId] as const,
  companies: ['companies'] as const,
  loans: ['loans'] as const,
  deliveries: ['deliveries'] as const,
  chat: ['chat'] as const,
  chatMessages: (channelId: string) => ['chat', channelId] as const,
  chatBlocks: ['chatBlocks'] as const,
  dmTargets: ['dmTargets'] as const,
};

export function useMe(enabled = true) {
  return useQuery({ queryKey: qk.me, queryFn: api.me, enabled, retry: false });
}

export function useWorld(enabled = true) {
  return useQuery({ queryKey: qk.world, queryFn: api.world, enabled, staleTime: 15_000 });
}

export function useHouse(enabled = true) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: qk.house,
    enabled,
    staleTime: 10_000,
    queryFn: async () => {
      const h = await api.house();
      // Seed the character cache so links and pickers resolve instantly.
      for (const m of h.members) qc.setQueryData(qk.character(m.id), m);
      return h;
    },
  });
}

export function useCharacter(id: Id | null | undefined) {
  return useQuery({ queryKey: qk.character(id ?? ''), queryFn: () => api.character(id!), enabled: !!id, staleTime: 30_000 });
}

export function useCharacters(ids: Id[]) {
  return useQueries({
    queries: ids.map((id) => ({ queryKey: qk.character(id), queryFn: () => api.character(id), staleTime: 30_000 })),
  });
}

export function useTitle(id: Id | null | undefined) {
  return useQuery({ queryKey: qk.title(id ?? ''), queryFn: () => api.title(id!), enabled: !!id, staleTime: 15_000 });
}

export function useHouseDetail(id: Id | null | undefined) {
  return useQuery({ queryKey: qk.houseDetail(id ?? ''), queryFn: () => api.houseDetail(id!), enabled: !!id, staleTime: 60_000 });
}

export function useChronicle(mine: boolean) {
  return useInfiniteQuery({
    queryKey: qk.chronicle(mine),
    initialPageParam: undefined as number | undefined,
    queryFn: ({ pageParam }) => api.chronicle({ before: pageParam, limit: 50, mine }),
    getNextPageParam: (last) => (last.length < 50 ? undefined : last[last.length - 1]?.id),
  });
}

/** Frontier regions plus what the viewer's house can do there (settle, charters, create titles). */
export function useFrontier(enabled = true) {
  return useQuery({ queryKey: qk.frontier, queryFn: api.frontier, enabled, staleTime: 10_000 });
}
export function useCities(enabled = true) {
  return useQuery({ queryKey: qk.cities, queryFn: api.cities, enabled, staleTime: 15_000 });
}
export function useMarket(cityId: Id | null | undefined) {
  return useQuery({ queryKey: qk.market(cityId ?? ''), queryFn: () => api.market(cityId!), enabled: !!cityId, staleTime: 10_000 });
}
export function useGuild(cityId: Id | null | undefined) {
  return useQuery({ queryKey: qk.guild(cityId ?? ''), queryFn: () => api.guild(cityId!), enabled: !!cityId, staleTime: 15_000 });
}
export function useCompanies(enabled = true) {
  return useQuery({ queryKey: qk.companies, queryFn: api.companies, enabled, staleTime: 15_000 });
}
export function useLoans(enabled = true) {
  return useQuery({ queryKey: qk.loans, queryFn: api.loans, enabled, staleTime: 15_000 });
}
export function useDeliveries(enabled = true) {
  return useQuery({ queryKey: qk.deliveries, queryFn: api.deliveries, enabled, staleTime: 10_000 });
}

// ------------------------------------------------------------------ chat

/** Channels the account can see, plus its own mute/ban state. */
export function useChatChannels(enabled = true) {
  return useQuery({ queryKey: qk.chat, queryFn: () => api.getChat(), enabled, staleTime: 10_000 });
}

/**
 * One page of a channel's messages, newest first, plus older pages via
 * `fetchPreviousPage` (the `before` cursor is the oldest message of the newest-known page).
 */
export function useChatMessages(channelId: string | null | undefined) {
  return useInfiniteQuery({
    queryKey: qk.chatMessages(channelId ?? ''),
    enabled: !!channelId,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => api.getChatMessages(channelId!, pageParam),
    // Pages only grow backwards (load-older): there is never a "next" page.
    getNextPageParam: () => undefined,
    getPreviousPageParam: (first: ChatMessagesResponse) => (first.hasMore ? first.messages[0]?.id : undefined),
  });
}

export function useChatBlocks(enabled = true) {
  return useQuery({ queryKey: qk.chatBlocks, queryFn: () => api.getChatBlocks(), enabled, staleTime: 15_000 });
}

export function useDmTargets(enabled = true) {
  return useQuery({ queryKey: qk.dmTargets, queryFn: () => api.getDmTargets(), enabled, staleTime: 15_000 });
}

export function useSendChatMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { channelId: string; text: string }) => api.sendChatMessage(v.channelId, v.text),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.chat }); // refresh last-message + unread for the channel list
    },
  });
}

/** Marks a channel read; refreshes the channel list so unread badges drop. */
export function useReadChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (channelId: string) => api.readChat(channelId),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: qk.chat }); },
  });
}
export function usePedigree(houseId: Id | null | undefined) {
  return useQuery({ queryKey: qk.pedigree(houseId ?? ''), queryFn: () => api.getPedigree(houseId!), enabled: !!houseId, staleTime: 60_000 });
}

export function useTitleHistory(id: Id | null | undefined) {
  return useQuery({ queryKey: qk.titleHistory(id ?? ''), queryFn: () => api.getTitleHistory(id!), enabled: !!id, staleTime: 30_000 });
}

export function useCharacterHistory(id: Id | null | undefined) {
  return useQuery({ queryKey: qk.characterHistory(id ?? ''), queryFn: () => api.getCharacterHistory(id!), enabled: !!id, staleTime: 30_000 });
}

export function useCharacterSearch(params: { q: string; alive?: boolean; houseId?: string; rank?: TitleRank; limit?: number }, enabled: boolean) {
  return useQuery({
    queryKey: ['characterSearch', params],
    queryFn: () => api.searchCharacters(params),
    enabled: enabled && params.q.trim().length > 0,
    staleTime: 30_000,
  });
}

export function useMarriageCandidates(params: { charId: string; dowry?: number; matrilineal?: boolean; minAge?: number; maxAge?: number; limit?: number }, enabled = true) {
  return useQuery({
    queryKey: ['marriageCandidates', params],
    queryFn: () => api.getMarriageCandidates(params),
    placeholderData: keepPreviousData, // dowry / matrilineal edits refresh verdicts in place, no list flash
    enabled: enabled && params.charId.length > 0,
    staleTime: 15_000,
  });
}

/** Lords a landless adult could offer knightly service to (ranked by the sim). */
export function useServiceCandidates(knightId: string, enabled = true) {
  return useQuery({
    queryKey: ['serviceCandidates', knightId],
    queryFn: () => api.getServiceCandidates({ knightId, limit: 300 }),
    enabled: enabled && knightId.length > 0,
    staleTime: 15_000,
  });
}

/** Lookup helpers derived from the world view. */
/** Knights a squire-age character could be sent to serve (ranked by the sim). */
export function useSquireCandidates(squireId: string, enabled = true) {
  return useQuery({
    queryKey: ['squireCandidates', squireId],
    queryFn: () => api.getSquireCandidates({ squireId, limit: 300 }),
    enabled: enabled && squireId.length > 0,
    staleTime: 15_000,
  });
}

export function useTitleIndex(): Map<Id, TitleSummary> {
  const { data } = useWorld();
  const map = new Map<Id, TitleSummary>();
  data?.titles.forEach((t) => map.set(t.id, t));
  return map;
}

export function invalidateGame(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: qk.world });
  void qc.invalidateQueries({ queryKey: qk.house });
  void qc.invalidateQueries({ queryKey: ['character'] });
  void qc.invalidateQueries({ queryKey: ['title'] });
  void qc.invalidateQueries({ queryKey: ['houseDetail'] });
  void qc.invalidateQueries({ queryKey: ['chronicle'] });
  void qc.invalidateQueries({ queryKey: ['pedigree'] });
  void qc.invalidateQueries({ queryKey: ['titleHistory'] });
  void qc.invalidateQueries({ queryKey: ['characterHistory'] });
  void qc.invalidateQueries({ queryKey: ['characterSearch'] });
  void qc.invalidateQueries({ queryKey: ['cities'] });
  void qc.invalidateQueries({ queryKey: qk.frontier });
  void qc.invalidateQueries({ queryKey: ['market'] });
  void qc.invalidateQueries({ queryKey: ['guild'] });
  void qc.invalidateQueries({ queryKey: ['companies'] });
  void qc.invalidateQueries({ queryKey: ['loans'] });
  void qc.invalidateQueries({ queryKey: qk.deliveries });
}

// ------------------------------------------------------------------ auth

export function useAuth() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { mode: 'login' | 'register'; email: string; password: string }) => {
      const res = v.mode === 'login'
        ? await api.login({ email: v.email, password: v.password })
        : await api.register({ email: v.email, password: v.password });
      await setToken(res.token);
      return res;
    },
    onSuccess: () => { void qc.invalidateQueries(); },
  });
}

export async function logout(qc: QueryClient) {
  await setToken(null);
  qc.clear();
}

export function useFoundHouse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.foundHouse,
    onSuccess: (me) => {
      qc.setQueryData(qk.me, me);
      invalidateGame(qc);
    },
  });
}

// ------------------------------------------------------------------ commands

const POLL_MS = 900;
const POLL_MAX = 70;

/**
 * Posts a command, then follows it until the tick loop has applied or rejected it.
 * `run()` resolves with the CommandResult (never throws); `error` holds the latest
 * rejection or transport message so forms can show it inline.
 */
export function useCommand() {
  const qc = useQueryClient();
  const { t } = useTranslation();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async (command: Command): Promise<CommandResult> => {
    setPending(true);
    setError(null);
    const ui = useUi.getState();
    try {
      let q = await api.postCommand(command);
      let polls = 0;
      while (q.status === 'queued' && polls < POLL_MAX) {
        await new Promise((r) => setTimeout(r, POLL_MS));
        q = await api.getCommand(q.id);
        polls++;
      }
      const result: CommandResult = q.result ?? (q.status === 'applied' ? { ok: true } : { ok: false, error: t('command.timeout') });
      if (q.status === 'queued') {
        ui.pushToast('info', t('command.stillQueued'));
      } else {
        if (!result.ok) setError(result.error ?? t('command.rejected'));
        const s = useUi.getState();
        if (!s.wsConnected && !s.toastedCommands.has(q.id)) {
          s.toastedCommands.add(q.id);
          s.pushToast(result.ok ? 'info' : 'error', result.ok ? t('command.done') : result.error ?? t('command.rejected'));
        }
      }
      invalidateGame(qc);
      return result;
    } catch (e) {
      const msg = e instanceof Error && e.message !== 'network' ? e.message : t('error.network');
      setError(msg);
      ui.pushToast('error', msg);
      return { ok: false, error: msg };
    } finally {
      setPending(false);
    }
  }, [qc, t]);

  return { run, pending, error, clearError: () => setError(null) };
}

/** Character that issues commands for the house: the head if present. */
export function actorOf(house: HouseView | undefined): Id {
  return house?.house.headId ?? house?.members[0]?.id ?? '';
}

export function memberIds(house: HouseView | undefined): Set<Id> {
  return new Set(house?.members.map((m) => m.id) ?? []);
}

export type { CharacterView };
